import { useState } from 'react'
import { useApps, useDeleteApp } from '@/hooks/useApps'
import { Button, SectionHeader, Skeleton, Empty } from '@/components/ui'
import { AppCard } from '@/components/AppCard'
import { Filters, applyFilters } from '@/components/Filters'
import { AppFormDialog } from '@/components/AppFormDialog'
import { useUIStore } from '@/stores/uiStore'
import { Plus, Boxes, Code2, ExternalLink } from 'lucide-react'
import { formatCurrency, formatRelativeDate, entityColor, statusColor, cn } from '@/lib/utils'
import { Link } from '@tanstack/react-router'
import { toast } from 'sonner'

export function PortfolioPage() {
  const { data: apps, isLoading } = useApps()
  const view = useUIStore((s) => s.view)
  const [adding, setAdding] = useState(false)

  const filtered = apps ? applyFilters(apps) : []

  return (
    <div className="space-y-6">
      <SectionHeader
        eyebrow="Portfolio"
        title={`${apps?.length ?? 0} apps · ${filtered.length} shown`}
        description="Filter, sort, search across every app. Click any card to open the detail view."
        action={
          <Button onClick={() => setAdding(true)}>
            <Plus size={14} /> New App
          </Button>
        }
      />

      <Filters />

      {isLoading ? (
        <div className="grid grid-cols-3 gap-4">
          {Array.from({ length: 9 }, (_, i) => <Skeleton key={i} className="h-56" />)}
        </div>
      ) : filtered.length === 0 ? (
        <Empty
          icon={<Boxes size={48} />}
          title="No apps match these filters"
          description="Adjust filters or seed Firestore from the Settings page."
          action={<Button variant="ghost" onClick={() => useUIStore.getState().reset()}>Clear filters</Button>}
        />
      ) : view === 'grid' ? (
        <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
          {filtered.map((a, i) => <AppCard key={a.slug} app={a} index={i} />)}
        </div>
      ) : (
        <PortfolioTable apps={filtered} />
      )}

      <AppFormDialog open={adding} onClose={() => setAdding(false)} />
    </div>
  )
}

function PortfolioTable({ apps }: { apps: import('@/lib/types').AppRecord[] }) {
  const del = useDeleteApp()

  async function handleDelete(id: string, name: string) {
    if (!confirm(`Delete ${name}? This is reversible only via the audit log.`)) return
    try {
      await del.mutateAsync(id)
      toast.success(`Deleted ${name}`)
    } catch (e) {
      toast.error((e as Error).message)
    }
  }

  return (
    <div className="glass rounded-lg overflow-hidden">
      <table className="w-full text-sm">
        <thead className="bg-navy/40 border-b border-bronze/20">
          <tr>
            <Th>Name</Th>
            <Th>Entity</Th>
            <Th>Status</Th>
            <Th>Stack</Th>
            <Th align="right">Heat</Th>
            <Th align="right">Value</Th>
            <Th align="right">Acq. midpoint</Th>
            <Th align="right">Updated</Th>
            <Th>Links</Th>
            <Th />
          </tr>
        </thead>
        <tbody>
          {apps.map((a) => {
            const eC = entityColor(a.entity)
            const sC = statusColor(a.status)
            return (
              <tr key={a.slug} className="border-b border-cream/5 hover:bg-cream/2 transition-colors">
                <Td>
                  <Link to="/apps/$slug" params={{ slug: a.slug }} className="font-heading text-cream hover:text-gold-soft">
                    {a.name}
                  </Link>
                  <div className="font-mono text-[10px] text-cream/40 mt-0.5">{a.slug}</div>
                </Td>
                <Td>
                  <span className={cn('font-mono text-[10px] uppercase tracking-[0.1em] px-2 py-0.5 rounded border', eC.bg, eC.text, eC.border)}>
                    {a.entity}
                  </span>
                </Td>
                <Td>
                  <span className={cn('inline-flex items-center gap-1.5 font-mono text-[10px] uppercase tracking-[0.1em]', sC.text)}>
                    <span className={cn('size-1 rounded-full', sC.dot)} />
                    {a.status}
                  </span>
                </Td>
                <Td><span className="text-cream/65 text-xs">{a.stack?.slice(0, 3).join(' · ')}</span></Td>
                <Td align="right"><span className="font-mono text-gold-soft">{a.score?.heat ?? '—'}</span></Td>
                <Td align="right"><span className="font-mono text-success">{a.score?.value ?? '—'}</span></Td>
                <Td align="right"><span className="font-mono text-cream">{formatCurrency(a.valuation?.acquisition.midpoint, { compact: true })}</span></Td>
                <Td align="right"><span className="text-cream/55 text-xs">{formatRelativeDate(a.last_modified)}</span></Td>
                <Td>
                  <div className="flex items-center gap-2">
                    {a.github_repo && <a href={a.github_repo} target="_blank" rel="noreferrer" className="text-cream/55 hover:text-gold-soft"><Code2 size={14} /></a>}
                    {a.firebase_site && <a href={`https://${a.firebase_site}.web.app`} target="_blank" rel="noreferrer" className="text-cream/55 hover:text-gold-soft"><ExternalLink size={14} /></a>}
                  </div>
                </Td>
                <Td>
                  {a.id && (
                    <button
                      className="text-[10px] uppercase tracking-[0.1em] font-mono text-danger/70 hover:text-danger"
                      onClick={() => handleDelete(a.id!, a.name)}
                    >
                      Del
                    </button>
                  )}
                </Td>
              </tr>
            )
          })}
        </tbody>
      </table>
    </div>
  )
}

function Th({ children, align = 'left' }: { children?: React.ReactNode; align?: 'left' | 'right' }) {
  return (
    <th className={cn('px-4 py-3 font-mono text-[10px] uppercase tracking-[0.15em] text-cream/55', align === 'right' && 'text-right')}>
      {children}
    </th>
  )
}

function Td({ children, align = 'left' }: { children?: React.ReactNode; align?: 'left' | 'right' }) {
  return <td className={cn('px-4 py-3 align-middle', align === 'right' && 'text-right')}>{children}</td>
}
